// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// ASP #14 — remove a prescription from "PA approved — not yet in CareRx", and bring it back.
// PioneerRx owns the prescription: nothing is deleted. A removal is recorded with its reason (mig 436),
// the feed skips the Rx, and Restore puts it back. Both are audited server-side.

import { useEffect, useState } from 'react'
import SidePanel from '../../components/common/SidePanel'
import { Pager } from '../../components/Pager'
import { formatUsDateTime } from '../../api/format'
import { friendlyError } from '../../api/errors'
import LanguageBadge from '../../components/ui/LanguageBadge'
import { useDismissCandidate, useDismissedCandidates, useRestoreCandidate } from '../../hooks/queries/carerxClinical'

const MAX_REASON = 500   // CareRxFeedDismissalFunctions.MaxReasonLength
const PAGE_SIZE = 10

const ghost = { fontSize: 12, padding: '4px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer' } as const
const primary = { fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer' } as const

/** The row's "Remove" action: asks why, then takes the prescription off the list. */
export function DismissCandidateButton({ rxId, label, onDone }: {
  rxId: string
  /** Who and what, for the panel's subtitle ("Sarah Johnson — Adalimumab · Rx 200001"). */
  label: string
  onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const dismiss = useDismissCandidate(() => { setOpen(false); setReason(''); onDone() })
  const text = reason.trim()
  return (
    <>
      <button style={ghost} onClick={() => { setErr(null); setOpen(true) }} title="Remove this prescription from the PA approved list">Remove</button>
      <SidePanel open={open} title="Remove from PA approved" subtitle={label} width={460} onClose={() => setOpen(false)}>
        <p style={{ fontSize: 13, color: 'var(--color-text-soft)', marginTop: 0 }}>
          The prescription stays in PioneerRx — it only leaves this list. You can restore it from
          <b> Removed from list</b> at any time.
        </p>
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, marginBottom: 4 }} htmlFor={`dismiss-${rxId}`}>Why is it being removed?</label>
        <textarea id={`dismiss-${rxId}`} value={reason} maxLength={MAX_REASON} onChange={e => setReason(e.target.value)}
          placeholder="e.g. Patient declined CareRx services"
          style={{ width: '100%', minHeight: 80, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text)', fontFamily: 'inherit' }} />
        {err && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button style={{ ...primary, opacity: text ? 1 : 0.55, cursor: text ? 'pointer' : 'default' }} disabled={!text || dismiss.isPending}
            onClick={() => dismiss.mutate({ rxId, reason: text }, { onError: e => setErr(friendlyError(e)) })}>Remove from list</button>
          <button style={ghost} onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </SidePanel>
    </>
  )
}

/** "Removed from list (n)": every removed prescription, newest first, paged on the server, with Restore. */
export function DismissedCandidatesPanel({ open, onClose, onRestored }: { open: boolean; onClose: () => void; onRestored: () => void }) {
  const [page, setPage] = useState(1)
  const [err, setErr] = useState<string | null>(null)
  const list = useDismissedCandidates(page, PAGE_SIZE, open)
  const restore = useRestoreCandidate(onRestored)
  const total = list.data?.total ?? 0
  // Restoring the last row on a page leaves that page empty: step back to the last page that exists.
  const lastPage = Math.max(1, list.data?.total_pages ?? 1)
  useEffect(() => { if (list.isSuccess && page > lastPage) setPage(lastPage) }, [list.isSuccess, page, lastPage])
  return (
    <SidePanel open={open} title="Removed from PA approved" subtitle={total ? `${total} prescription${total === 1 ? '' : 's'}` : null} width={620} onClose={onClose}>
      {err && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{err}</div>}
      {list.isError && <div style={{ fontSize: 12, color: '#dc2626' }}>{friendlyError(list.error)}</div>}
      {list.isSuccess && total === 0 && <div style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>Nothing has been removed from the list.</div>}
      {(list.data?.items ?? []).map(d => (
        <div key={d.rx_id} style={{ padding: '9px 0', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, fontSize: 13 }}>
            <div><b>{d.patient_name || 'Patient'}</b><LanguageBadge language={d.preferred_language} /> — {d.drug_name || 'drug'} · <span style={{ fontFamily: 'ui-monospace, monospace' }}>Rx {d.rx_number || '—'}</span></div>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{d.dismissal_reason}</div>
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 2 }}>Removed by {d.dismissed_by_name || 'a user'} · {formatUsDateTime(d.dismissed_at)}</div>
          </div>
          <button style={ghost} disabled={restore.isPending}
            onClick={() => { setErr(null); restore.mutate(d.rx_id, { onError: e => setErr(friendlyError(e)) }) }}>Restore</button>
        </div>
      ))}
      <Pager page={page} totalPages={list.data?.total_pages ?? 1} total={total} pageSize={PAGE_SIZE} onPage={setPage} showPageSize={false} label="prescriptions" />
    </SidePanel>
  )
}
